import { useEffect, useState, useMemo } from "react";
import { LoadingSpinner } from "../../components/compIndex";
import { Pagination } from "../../components/compIndex";

const fieldMappings = {
  "tradable.tradeValue": "Trade Value",
  cooldownMs: "Cooldown (MS)",
  uses: "Item Uses",
  description: "Description",
  created: "Created",
  removed: "Removed",
  name: "Name Change",
  craftable: "Made Craftable",
  "craftable.requiredTier": "Tier Change",
  "itemUses.newItems": "New Recipe Uses",
  "craftable.autoGrant": "Recipe Available",
  "categories.newItems": "Category Change",
  "crop.growTime": "Crop Grow Time",
  "craftable.requiredLevel": "Required Level",
  "craftable.requiredSkill": "Required Skill",
  "placeObject.image": "Image",
  "image": "Image",
  "craftingMap.newItems": "Crafting Items"
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const day = date.getDate();
  const suffix = day % 10 === 1 && day !== 11 ? "st" : day % 10 === 2 && day !== 12 ? "nd" : day % 10 === 3 && day !== 13 ? "rd" : "th";
  return `${date.toLocaleDateString("en-US", { month: "short" })} ${day}${suffix}, ${date.getFullYear()}`;
};

const cleanFieldName = (field) => {
  const cleanField = field
    .replace(/\.\d+$/, "")
    .replace(/\.before$/, "")
    .replace(/\.after$/, "");

  const displayName = fieldMappings[cleanField] || cleanField;

  if (field.includes(".before")) {
    return `${displayName} (Before)`;
  }

  if (field.includes(".after")) {
    return `${displayName} (After)`;
  }

  return displayName;
};

export default function ChangeLog() {
  const [logs, setLogs] = useState(null);
  const [items, setItems] = useState(null);
  const [achievements, setAchievements] = useState(null);
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState({ start: null, end: null });
  const [fieldFilter, setFieldFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1); //Pagination
  const [itemsPerPage] = useState(24); //Pagination

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [logsRes, itemsRes, achievementsRes] = await Promise.all([
          fetch("https://api.pixelore.wiki/api/changelogs").then((res) => res.json()),
          fetch("https://api.pixelore.wiki/api/items").then((res) => res.json()),
          fetch("https://api.pixelore.wiki/api/pixelachievements").then((res) => res.json()),
        ]);

        setLogs(logsRes);
        setItems(Object.fromEntries(itemsRes.map((item) => [item.id, item.name])));
        setAchievements(Object.fromEntries(achievementsRes.map((ach) => [ach.id, ach.name])));
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const flattenChanges = (changes, prefix = "") => {
    // Guard against null/undefined changes
    if (!changes || typeof changes !== "object") {
      return [];
    }

    return Object.entries(changes).flatMap(([key, value]) => {
      const fullPath = prefix ? `${prefix}.${key}` : key;

      // Guard against null values
      if (value === null || value === undefined) {
        return [{ field: fullPath, value: null }];
      }

      if (typeof value === "object") {
        // Handle arrays (including newItems special case)
        if (Array.isArray(value)) {
          if (key === "newItems") {
            return value.map((item) => {
              // Handle object items (like recipe changes with images)
              if (item && typeof item === "object") {
                return {
                  field: fullPath,
                  achievementImage: item.achievementImage || "",
                  name: item.name || "Unknown Item",
                };
              }
              // Handle simple values (like category strings)
              return {
                field: fullPath,
                simpleValue: item,
              };
            });
          } else {
            return value.flatMap((item, index) => {
              if (item === null || item === undefined) {
                return [{ field: `${fullPath}[${index}]`, value: null }];
              }
              if (typeof item === "object") {
                return flattenChanges(item, `${fullPath}[${index}]`);
              }
              return [{ field: `${fullPath}[${index}]`, value: item }];
            });
          }
        }

        // Handle before/after at any nesting level
        if ("before" in value || "after" in value) {
          return {
            field: fullPath,
            before: value.before ?? null,
            after: value.after ?? null,
          };
        }

        // Recursively flatten nested objects
        return flattenChanges(value, fullPath);
      }

      // Handle primitive values
      return { field: fullPath, value };
    });
  };

  const filteredLogs = useMemo(() => {
    const flattenKeys = (obj, prefix = "") =>
      Object.entries(obj).flatMap(([key, value]) => (typeof value === "object" && value !== null ? flattenKeys(value, `${prefix}${key}.`) : `${prefix}${key}`));

    if (!logs || !items || !achievements) return [];

    const seenLogs = new Set();

    return logs
      .filter((log) => {
        const itemName = items[log.id] || achievements[log.id] || log.id;
        if (!itemName || itemName === "null") return false;

        const logKey = `${log.id}-${log.timestamp}`;
        if (seenLogs.has(logKey)) return false;
        seenLogs.add(logKey);

        const startDate = new Date(dateFilter.start);
        const endDate = new Date(dateFilter.end);
        const logDate = new Date(log.timestamp);

        const matchesDate = (!dateFilter.start || logDate >= startDate) && (!dateFilter.end || logDate <= endDate);

        const matchesSearch = itemName.toLowerCase().includes(search.toLowerCase()) || log.id.toLowerCase().includes(search.toLowerCase());

        const fieldPaths = flattenKeys(log.changes);

        const matchesField =
          !fieldFilter ||
          fieldPaths.some((fieldPath) => {
            const mappedField = fieldMappings[fieldPath] || fieldPath;
            return mappedField.toLowerCase().includes(fieldFilter.toLowerCase());
          });

        return matchesSearch && matchesDate && matchesField;
      })
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }, [logs, items, achievements, search, dateFilter, fieldFilter]);

  const getChangeTypeLabel = (type) => {
    if (type === "achievement") {
      return "Recipe";
    }
    if (type === "item") {
      return "Item";
    }
    return "General";
  };

  const clearFilters = () => {
    setSearch("");
    setDateFilter({ start: null, end: null });
    setFieldFilter("");
  };

  //handle Pagination
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const renderImageWithName = (item) => {
    return (
      <div className="flex items-center mt-1">
        {item.achievementImage && (
          <img src={item.achievementImage} alt={item.name || "Achievement Image"} className="w-6 h-6 object-cover mr-2" />
        )}
        <span className="text-sm">{item.name || "Unknown Item"}</span>
      </div>
    );
  };
  
  const renderBeforeAfter = (before, after) => {
    return (
      <div>
        {before !== undefined && (
          <div className="mb-2">
            <span className="font-semibold">Before:</span>
            {typeof before === "object" && before?.achievementImage ? (
              renderImageWithName(before)
            ) : (
              <span className=" text-xs"> {before === null ? "null" : before.toLocaleString()}</span>
            )}
          </div>
        )}
        {after !== undefined && (
          <div className="mb-2">
            <span className="font-semibold">After:</span>
            {typeof after === "object" && after?.achievementImage ? (
              renderImageWithName(after)
            ) : (
              <span className=" text-xs"> {after === null ? "null" : after.toLocaleString()}</span>
            )}
          </div>
        )}
      </div>
    );
  };

  // Calculate pagination
  const totalPages = Math.ceil(filteredLogs.length / itemsPerPage);
  const maxPageNumbers = 5;
  const halfPageRange = Math.floor(maxPageNumbers / 2);

  let startPage = Math.max(1, currentPage - halfPageRange);
  let endPage = Math.min(totalPages, startPage + maxPageNumbers - 1);

  if (endPage - startPage + 1 < maxPageNumbers) {
    startPage = Math.max(1, endPage - maxPageNumbers + 1);
  }

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  if (!logs || !items || !achievements) {
    return <LoadingSpinner />;
  }

  return (
    <div className="container mx-auto mt-5 p-6 rounded-lg shadow-lg panel-style">
      <h1 className="panel-heading">Change Logs</h1>

      {/* Change Log Filters */}
      <div className="filters-panel p-3 m-2">
        <div className="text-xs md:flex md:flex-row justify-between">
          {/* 🔍 Search Bar */}
          <input
            type="text"
            placeholder="Search for an item..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border p-2 mb-4 w-full md:w-1/4 select-forms"
          />

          {/* Date Filter */}
          <div className="flex space-x-4 mb-4">
            <input
              type="date"
              value={dateFilter.start || ""}
              onChange={(e) => setDateFilter((prev) => ({ ...prev, start: e.target.value }))}
              className="border p-2 select-forms"
            />
            <span className="my-auto">to</span>
            <input
              type="date"
              value={dateFilter.end || ""}
              onChange={(e) => setDateFilter((prev) => ({ ...prev, end: e.target.value }))}
              className="border p-2 select-forms"
            />
          </div>

          {/* Field Filter */}
          <select value={fieldFilter} onChange={(e) => setFieldFilter(e.target.value)} className="border p-2 mb-4 w-full md:w-1/4 text-xs select-forms">
            <option value="">All Fields</option>
            {Object.keys(fieldMappings).map((key) => (
              <option key={key} value={key}>
                {fieldMappings[key]}
              </option>
            ))}
          </select>

          <button
            onClick={clearFilters}
            className="w-fit h-fit p-2 panel-button font-semibold text-xs rounded-lg"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Change Log Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 gap-1 m-2">
        {filteredLogs.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((log) => {
          const changes = log.changes;
          const itemName = items[log.id] || achievements[log.id] || log.id;
          const changeTypeLabel = getChangeTypeLabel(log.type);

          return (
            <div key={log._id} className="component-style">
              <h2 className="font-body font-semibold">{itemName}</h2>
              <p className="text-[var(--text3-color)] text-xs">
                <span className="font-semibold">{changeTypeLabel} Change</span> - Changed on: {formatDate(log.timestamp)}
              </p>
          
              <div className="p-4 border rounded-lg shadow-lg bg-[var(--entity-mini)] mt-4 text-xs">
                {/* Check if the changes object has a "created" field at the root level */}
                {changes.created && (
                  <p className="text-green-500 text-xs font-bold">🆕 This {changeTypeLabel} was newly created!</p>
                )}
          
                {/* Iterate over flattened changes */}
                {flattenChanges(changes).map((change, idx) => {
                  const displayField = cleanFieldName(change.field);
          
                  // Guard against null/undefined change or fields under "created"
                  if (!change || change.field === "id" || change.field.startsWith("created.")) return null;
          
                  // Determine the body content based on the change type
                  let bodyContent = null;
          
                  // Handle special cases like "removed"
                  if (change.field === "removed") {
                    bodyContent = <p className="text-red-500 text-xs font-bold">❌ This {changeTypeLabel} was removed</p>;
                  }
          
                  // Handle craftable.autoGrant
                  else if (change.field === "craftable.autoGrant") {
                    if (change.before === null && change.after === true) {
                      bodyContent = <div className="text-green-600 font-semibold text-xs">✨ This {changeTypeLabel} is now craftable!</div>;
                    } else if (change.before === true && change.after === null) {
                      bodyContent = <div className="text-red-600 font-semibold text-xs">🚫 This {changeTypeLabel} is no longer craftable</div>;
                    } else if (change.before === null && change.after === false) {
                      bodyContent = <div className="text-yellow-600 font-semibold text-xs">⚠️ This {changeTypeLabel} is not craftable</div>;
                    }
                  }
          
                  // Handle newItems
                  else if (change.field.endsWith("newItems")) {
                    bodyContent = (
                      <div className="flex items-center mb-2">
                        {change.achievementImage && (
                          <img src={change.achievementImage.replace("cdn:/", "https://d31ss916pli4td.cloudfront.net/") ?? change.achievementImage} alt={change.name || "Achievement Image"} className="w-6 h-6 object-cover mr-2" />
                        )}
                        <span className="font-semibold text-green-600 text-xs">{change.name || change.simpleValue || "Unknown Item"}</span>
                      </div>
                    );
                  }
          
                  // Handle before/after values
                  else {
                    bodyContent = renderBeforeAfter(change.before, change.after);
                  }
          
                  // Single return statement with one heading and conditional body
                  return (
                    <div key={`${log._id}-${idx}`}>
                      <h2 className="text-base font-teko font-semibold text-[var(--text3-color)]">{displayField}</h2>
                      {bodyContent}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <Pagination totalPages={totalPages} currentPage={currentPage} onPageChange={handlePageChange} startPage={startPage} endPage={endPage} />
    </div>
  );
}
